import React, { useMemo, useRef, useState } from 'react';
import type { ConsensusTableWidgetInstanceConfig, ConsensusTableData } from './types';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner, WidgetEmptyState } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { AgGridWrapper } from '../../../blocks/tables';
import type { ColDef } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

// Sample data shown during dashboard construction (no asset assigned).
// Mirrors the normalized InSync shape: Format H=heading, N=USD Mn,
// P=percent fraction, X=ratio; periods as 1Q26A (actual) / 2Q26E (estimate).
const SAMPLE_DATA: ConsensusTableData = {
  consensus_data: [
    { LineItem: 'Income Statement', Format: 'H', Sequence: 1, '4Q25A': null, '1Q26A': null, '2Q26E': null, FY26E: null },
    { LineItem: 'Total Revenue', Format: 'N', Sequence: 2, '4Q25A': 7658, '1Q26A': 10253, '2Q26E': 11264, FY26E: 49621 },
    { LineItem: 'EBITDA', Format: 'N', Sequence: 3, '4Q25A': 3048, '1Q26A': 2746, '2Q26E': 3266, FY26E: 14681 },
    { LineItem: 'Diluted EPS', Format: 'N', Sequence: 4, '4Q25A': 1.42, '1Q26A': 0.84, '2Q26E': 1.09, FY26E: 5.1 },
    { LineItem: 'ETR', Format: 'P', Sequence: 5, '4Q25A': 0.13, '1Q26A': 0.148, '2Q26E': 0.14, FY26E: 0.13 },
  ],
};

interface Props {
  config: ConsensusTableWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

interface PeriodCol {
  field: string;
  isAnnual: boolean;
  kind: 'A' | 'E';
}

// Normalized InSync periods: "1Q26A"/"4Q25E" quarters, "FY26A"/"FY26E" annuals.
// FMP fallback emits "FY2025" (consensus annuals, no suffix) — treated as E.
const parsePeriodCol = (col: string): PeriodCol | null => {
  let m = /^[1-4]Q\d{2}([AE])$/.exec(col);
  if (m) return { field: col, isAnnual: false, kind: m[1] as 'A' | 'E' };
  m = /^FY\d{2}([AE])$/.exec(col);
  if (m) return { field: col, isAnnual: true, kind: m[1] as 'A' | 'E' };
  if (/^FY\d{4}$/.test(col)) return { field: col, isAnnual: true, kind: 'E' };
  return null;
};

// Compact USD: 11263.68 (Mn) pre-scaled to dollars -> "$11.3B".
const compactUsd = (n: number): string => {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const PER_SHARE_RE = /\bEPS\b|per share|\bDPS\b/i;
const SHARE_COUNT_RE = /\bshares\b/i;

// InSync rows: values are USD Mn except per-share; P rows are fractions.
// Exported for tests.
export const formatInsyncValue = (raw: unknown, lineItem: string, format: string): string => {
  if (raw == null || raw === '') return '';
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return '';
  if (format === 'P') return `${(n * 100).toFixed(1)}%`;
  if (format === 'X') return n.toFixed(2);
  if (PER_SHARE_RE.test(lineItem)) return `$${n.toFixed(2)}`;
  if (SHARE_COUNT_RE.test(lineItem)) return Math.round(n).toLocaleString('en-US');
  return compactUsd(n * 1e6);
};

// Legacy/FMP rows (raw dollars, no Format column) keep the original behavior.
const formatLegacyValue = (raw: unknown, isPerShare: boolean): string => {
  if (raw == null || raw === '') return '';
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  if (isPerShare) return `$${n.toFixed(2)}`;
  return compactUsd(n);
};

const ConsensusTableWidgetRenderer: React.FC<Props> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<ConsensusTableData>(
    'consensus_table',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;
  const rows = useMemo(
    () => displayData?.consensus_data ?? [],
    [displayData],
  );
  const isInsync = rows.length > 0 && 'Format' in rows[0];

  // Period columns in delivered (chronological) order.
  const { labelField, quarterCols, annualCols } = useMemo(() => {
    if (!rows.length) return { labelField: '', quarterCols: [] as PeriodCol[], annualCols: [] as PeriodCol[] };
    const keys = Object.keys(rows[0]);
    const periods = keys.map(parsePeriodCol).filter((p): p is PeriodCol => p !== null);
    return {
      labelField: keys[0],
      quarterCols: periods.filter((p) => !p.isAnnual),
      annualCols: periods.filter((p) => p.isAnnual),
    };
  }, [rows]);

  const hasQuarters = quarterCols.length > 0;
  const [view, setView] = useState<'Quarterly' | 'Annual'>('Quarterly');
  const activeView = hasQuarters ? view : 'Annual';

  const gridRef = useRef<AgGridReact>(null);
  const handleDownload = () => {
    gridRef.current?.api?.exportDataAsCsv({
      fileName: `${uid ?? 'consensus'}_Consensus_${activeView}.csv`,
      processCellCallback: (p) => p.value, // raw USD Mn / fractions, not the $B-compacted display
    });
  };

  // Window: trailing actuals + forward estimates around "now".
  const visibleCols = useMemo(() => {
    const cols = activeView === 'Quarterly' ? quarterCols : annualCols;
    const trailing = activeView === 'Quarterly' ? 4 : 3;
    const forward = activeView === 'Quarterly' ? 4 : 3;
    const actuals = cols.filter((c) => c.kind === 'A');
    const estimates = cols.filter((c) => c.kind === 'E');
    return [...actuals.slice(-trailing), ...estimates.slice(0, forward)];
  }, [activeView, quarterCols, annualCols]);

  const columnDefs = useMemo<ColDef[]>(() => {
    if (!rows.length || !visibleCols.length) return [];
    return [
      {
        field: labelField,
        headerName: 'Line Item',
        pinned: 'left' as const,
        sortable: false,
        filter: false,
        cellClass: 'font-medium',
        tooltipField: labelField,
        minWidth: 150,
        width: 190,
      },
      ...visibleCols.map((p) => ({
        field: p.field,
        headerName: p.field,
        headerClass: p.kind === 'E' ? 'text-primary-600' : undefined,
        sortable: false,
        filter: false,
        type: 'rightAligned' as const,
        cellClass: 'tabular-nums',
        flex: 1,
        minWidth: 72,
        valueFormatter: (params: { value: unknown; data?: Record<string, unknown> }) => {
          const item = String(params.data?.[labelField] ?? '');
          return isInsync
            ? formatInsyncValue(params.value, item, String(params.data?.Format ?? ''))
            : formatLegacyValue(params.value, item.toUpperCase() === 'EPS');
        },
      })),
    ];
  }, [rows, visibleCols, labelField, isInsync]);

  if (loading) return <WidgetLoadingState size="sm" />;
  if (error) return <WidgetErrorState message={error} onRetry={refetch} />;
  if (uid && !rows.length) {
    return (
      <WidgetEmptyState
        title="No consensus estimates"
        description="No consensus data is available for this security."
      />
    );
  }
  if (!rows.length) return null;

  return (
    <div className={`h-full flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-surface shadow-sm ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}
      {/* Header — title left, controls right (ml-auto), matching the Valuation
          Table / Comps table-widget headers and the standard capsule toggle. */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-neutral-100 shrink-0">
        <span className="text-sm font-semibold text-neutral-800">Consensus Estimates</span>
        <div className="ml-auto flex items-center gap-2">
          {hasQuarters && (
            <div className="flex items-center gap-1 rounded-xl border border-neutral-200 bg-neutral-50 p-1">
              {(['Quarterly', 'Annual'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                    v === activeView
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={handleDownload}
            title="Download CSV"
            aria-label="Download CSV"
            className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
          >
            <FontAwesomeIcon icon={faDownload} className="text-xs" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <AgGridWrapper
          ref={gridRef}
          containerClassName="ag-theme-alpine ag-compact h-full w-full"
          rowData={rows}
          columnDefs={columnDefs}
          rowHeight={30}
          headerHeight={28}
          suppressCellFocus
          getRowClass={(params) =>
            String((params.data as Record<string, unknown> | undefined)?.Format ?? '') === 'H'
              ? 'font-semibold bg-neutral-50'
              : undefined
          }
        />
      </div>
      {isInsync && (
        <div className="shrink-0 px-3 pb-1.5 pt-0.5 flex items-center justify-between">
          <span className="text-[9px] uppercase tracking-wide text-neutral-400">
            USD Mn except per-share · A = actual, E = consensus
          </span>
          <span className="text-[9px] uppercase tracking-wide text-neutral-400">Source: InSync</span>
        </div>
      )}
    </div>
  );
};

export default ConsensusTableWidgetRenderer;
